import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";
import { useForm, useWatch } from "react-hook-form";
import {
  getRequiredFields,
  removeEmptyFields,
} from "@/lib/utils";
import {
  ACCOUNT_CATEGORY,
  ACCOUNT_NO,
  ALTERNATE_ACC,
  ALTERNATE_ACC_CUSTOM,
  CLEARING_ACCOUNT,
  COMMENT,
  COST_CENTER,
  GROUP_GL,
  LONG_DESC,
  NO_DIRECT_POSTING,
  ORDER_NUMBER,
  PROFIT_CENTER,
  RECON_ACCOUNT,
  SHORT_DESC,
  SUBLEDGER,
  VIRTUAL_ACC,
  OTHER_ALTERNATE_ACC,
} from "./consts";
import { useEffect, useMemo, useState } from "react";
import { toast } from "@/components/Toast";
import {
  useAddGlMasterDataMutation,
  useGetEnumDropdown,
  useGetGlMasterData,
  useUpdateGlMasterDataMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { UseAddEditGlMasterProps } from "./types";

const useAddEditGlMaster = ({
  rowSelected,
  onCloseDialog,
}: UseAddEditGlMasterProps) => {
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [keys, setKeys] = useState<any>({
    alternativeAcc: Math.random() * 3,
  });

  const form = useForm<formValues>({
    defaultValues: {
      [ACCOUNT_NO]: "",
      [ACCOUNT_CATEGORY]: "",
      [SUBLEDGER]: "",
      // [SUBLEDGER_TYPE]: "",
      [SHORT_DESC]: "",
      [LONG_DESC]: "",
      [COMMENT]: "",
      [RECON_ACCOUNT]: false,
      [GROUP_GL]: false,
      [PROFIT_CENTER]: false,
      [ORDER_NUMBER]: false,
      [COST_CENTER]: false,
      [NO_DIRECT_POSTING]: false,
      [CLEARING_ACCOUNT]: false,
      [VIRTUAL_ACC]: "",
      [ALTERNATE_ACC]: "",
      [ALTERNATE_ACC_CUSTOM]: "",
    },
    resolver: zodResolver(schema),
  });
  const queryClient = useQueryClient();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const id = communityData?.communityId;
  const formData = useWatch({ control: form.control });
  const isAlternateOther = formData?.[ALTERNATE_ACC] === OTHER_ALTERNATE_ACC;

  const requiredFields = getRequiredFields(schema);

  const {data: virtualAccList, isLoading: isLoadingVirtualAccList} = useGetEnumDropdown({
    enumName: "SystemAccountCode"
  })

  const { mutate: addInfo, isPending: isAddPending } =
    useAddGlMasterDataMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["gl-master-data", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["gl-master-dropdown", id],
        });
        onCloseDialog && onCloseDialog();
        toast.success("GL Info Added Successfully");
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateGlMasterDataMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["gl-master-data", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["gl-master-dropdown", id],
        });
        onCloseDialog && onCloseDialog();
        toast.success("GL Info updated Successfully");
      },
    });

  const resolveAlternateAccount = (data: any) => {
    if (data?.[ALTERNATE_ACC] === OTHER_ALTERNATE_ACC) {
      return data?.[ALTERNATE_ACC_CUSTOM] || "";
    }
    return data?.[ALTERNATE_ACC] || "";
  };

  const onSubmit = (data: any) => {
    const _data = data;

    if (!id) {
      toast.error(
        "Please select community"
      );
    }

    delete _data[PROFIT_CENTER]; 

    const nextAlternateAcc = resolveAlternateAccount(_data);
    if (rowSelected && String(rowSelected[ALTERNATE_ACC] || "") != String(nextAlternateAcc || "")) {
      setOpenConfirmation(true);
    } else {
      apiCall();
    }
  };

  const apiCall = () => {
    let _data = removeEmptyFields(formData);
    _data[ALTERNATE_ACC] = resolveAlternateAccount(formData);
    delete _data[ALTERNATE_ACC_CUSTOM];
    
    if (!_data[SUBLEDGER]) {
      delete _data[SUBLEDGER]
    }

    if (rowSelected) {
      updateInfo({
        id: rowSelected?.glMasterId,
        data: { ..._data, [VIRTUAL_ACC]: _data[VIRTUAL_ACC] ? _data[VIRTUAL_ACC] : null, communityId: id },
      });
    } else {
      addInfo({ data: { ..._data, [VIRTUAL_ACC]: _data[VIRTUAL_ACC] ? _data[VIRTUAL_ACC] : null, communityId: id } });
    }
  };

  // get gl master data
  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: id as string,
  });

  const alternateOptions = useMemo(() => {
    const org = (glData?.data || [])
      ?.sort((a: any, b: any) =>
        a.glAccountNumber?.localeCompare(b.glAccountNumber)
      )
      ?.map((data: any) => {
        return {
          label: data?.alternativeAccount
            ? `${data?.alternativeAccount}`
            : "",
          value: data?.alternativeAccount?.toString(),
        };
      })
      ?.filter((data: any) => data.label)
      .filter(
        (item: any, index: any, arr: any) =>
          arr.findIndex((i: any) => i.value === item.value) ===
          index
      )

      return [
        { label: "None", value: "" },
        ...org,
        { label: "Other", value: OTHER_ALTERNATE_ACC },
      ];
  }, [glData]);

  useEffect(() => {
    if (!rowSelected) return;

    const existingAlt = rowSelected?.[ALTERNATE_ACC]?.toString() || "";
    const knownValues = new Set(
      (glData?.data || [])
        .map((data: any) => data?.alternativeAccount?.toString())
        .filter(Boolean)
    );
    const isKnown = !existingAlt || knownValues.has(existingAlt);

    form.reset({
      [ACCOUNT_NO]: rowSelected?.[ACCOUNT_NO] || "",
      [ACCOUNT_CATEGORY]: rowSelected?.[ACCOUNT_CATEGORY] || "",
      [SUBLEDGER]: rowSelected?.[SUBLEDGER] || "",
      [SHORT_DESC]: rowSelected?.[SHORT_DESC] || "",
      [LONG_DESC]: rowSelected?.[LONG_DESC] || "",
      [COMMENT]: rowSelected?.[COMMENT] || "",
      [GROUP_GL]: rowSelected?.[GROUP_GL] || false,
      [RECON_ACCOUNT]: rowSelected?.[RECON_ACCOUNT] || false,
      [PROFIT_CENTER]: rowSelected?.[PROFIT_CENTER] || false,
      [ORDER_NUMBER]: rowSelected?.[ORDER_NUMBER] || false,
      [COST_CENTER]: rowSelected?.[COST_CENTER] || false,
      [NO_DIRECT_POSTING]: rowSelected?.[NO_DIRECT_POSTING] || false,
      [CLEARING_ACCOUNT]: rowSelected?.[CLEARING_ACCOUNT] || false,
      [VIRTUAL_ACC]: rowSelected?.[VIRTUAL_ACC] || "",
      [ALTERNATE_ACC]: isKnown ? existingAlt : OTHER_ALTERNATE_ACC,
      [ALTERNATE_ACC_CUSTOM]: isKnown ? "" : existingAlt,
    });
    setKeys({
      alternativeAcc: Math.random() * 3,
    });
  }, [rowSelected?.glMasterId, Boolean(glData?.data)]);

  return {
    form,
    requiredFields,
    onSubmit,
    isAddPending,
    isUpdatePending,
    isLoadingGlMaster,
    glData,
    openConfirmation,
    setOpenConfirmation,
    apiCall,
    keys,
    alternateOptions,
    isAlternateOther,
    isLoadingVirtualAccList,
    virtualAccList: virtualAccList?.data || []
  };
};

export default useAddEditGlMaster;
